import { get, noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { DELIVERY_WAREHOUSES_GLOBAL_TAG } from '#shared/api/globalTags';
import { apiFormDataRequest, apiV1Request } from '#shared/api/requests';
import {
    getOpenApiCancelExcelImportProductsUrl,
    getOpenApiGetIntegrationMappingSettingsUrl,
    getOpenApiGetMappingDiscoveryResultUrl,
    getOpenApiIntegrationsStartTypeUrl,
    getOpenApiSetIntegrationMappingSettingsUrl,
    getOpenApiStartDryMappingIntegrationUrl,
    getOpenApiStartFeedbacksSyncUrl,
    getOpenApiStartMappingDiscoveryUrl,
    getOpenApiStartMappingIntegrationUrl,
    getOpenApiUpdateIntegrationCredentialsUrl,
    getOpenApiUpdateIntegrationFeedbacksSettingsUrl,
    OpenApiCancelExcelImportProductsMethod,
    type OpenApiCancelExcelImportProductsResponseSuccess,
    type OpenApiDeleteIntegrationSettingsResponseSuccess,
    OpenApiGetIntegrationFeedbacksSettingsMethod,
    type OpenApiGetIntegrationFeedbacksSettingsRequest,
    type OpenApiGetIntegrationFeedbacksSettingsResponseSuccess,
    OpenApiGetIntegrationFeedbacksSettingsUrl,
    OpenApiGetIntegrationMappingSettingsMethod,
    type OpenApiGetIntegrationMappingSettingsPathParameters,
    type OpenApiGetIntegrationMappingSettingsResponseSuccess,
    type OpenApiGetIntegrationOrdersSettingsRequest,
    type OpenApiGetIntegrationOrdersSettingsResponseSuccess,
    type OpenApiGetIntegrationPricesSettingsRequest,
    type OpenApiGetIntegrationPricesSettingsResponseSuccess,
    type OpenApiGetIntegrationProductsSettingsRequest,
    type OpenApiGetIntegrationProductsSettingsResponseSuccess,
    type OpenApiGetIntegrationStocksSettingsRequest,
    type OpenApiGetIntegrationStocksSettingsResponseSuccess,
    OpenApiGetIntegrationsTypeStatusMethod,
    type OpenApiGetIntegrationsTypeStatusResponseSuccess,
    type OpenApiGetIntegrationsTypeStatusSearchParams,
    OpenApiGetIntegrationsTypeStatusUrl,
    type OpenApiGetIntegrationWarehousesResponseSuccess,
    type OpenApiGetIntegrationWarehousesSearchParams,
    OpenApiGetLastExternalModulesMethod,
    type OpenApiGetLastExternalModulesRequest,
    type OpenApiGetLastExternalModulesResponseSuccess,
    OpenApiGetLastExternalModulesUrl,
    OpenApiGetMappingDiscoveryResultMethod,
    type OpenApiGetMappingDiscoveryResultPathParameters,
    type OpenApiGetMappingDiscoveryResultResponseSuccess,
    type OpenApiGetPublicAPIKeyResponseSuccess,
    type OpenApiGetPublicAPIKeySearchParams,
    type OpenApiImportProductsAsyncResponses,
    type OpenApiImportProductsAsyncResponseSuccess,
    OpenApiIntegrationsStartTypeMethod,
    type OpenApiIntegrationsStartTypePathParameters,
    type OpenApiIntegrationsStartTypeResponseSuccess,
    OpenApiMerchantCenterFeedSyncMethod,
    type OpenApiMerchantCenterFeedSyncRequest,
    type OpenApiMerchantCenterFeedSyncResponseSuccess,
    OpenApiMerchantCenterFeedSyncUrl,
    type OpenApiSchemaIntegrationSyncType,
    OpenApiSetIntegrationMappingSettingsMethod,
    type OpenApiSetIntegrationMappingSettingsPathParameters,
    type OpenApiSetIntegrationMappingSettingsRequest,
    type OpenApiSetIntegrationMappingSettingsResponseSuccess,
    OpenApiStartDryMappingIntegrationContentType,
    OpenApiStartDryMappingIntegrationMethod,
    type OpenApiStartDryMappingIntegrationPathParameters,
    type OpenApiStartDryMappingIntegrationRequest,
    type OpenApiStartDryMappingIntegrationResponseSuccess,
    OpenApiStartFeedbacksSyncMethod,
    type OpenApiStartFeedbacksSyncPathParameters,
    type OpenApiStartFeedbacksSyncResponseSuccess,
    OpenApiStartMappingDiscoveryMethod,
    type OpenApiStartMappingDiscoveryPathParameters,
    type OpenApiStartMappingDiscoveryResponseSuccess,
    OpenApiStartMappingIntegrationMethod,
    type OpenApiStartMappingIntegrationPathParameters,
    type OpenApiStartMappingIntegrationResponseSuccess,
    OpenApiUpdateIntegrationCredentialsMethod,
    type OpenApiUpdateIntegrationCredentialsPathParameters,
    type OpenApiUpdateIntegrationCredentialsRequest,
    type OpenApiUpdateIntegrationCredentialsResponseSuccess,
    OpenApiUpdateIntegrationFeedbacksSettingsMethod,
    type OpenApiUpdateIntegrationFeedbacksSettingsPathParameters,
    type OpenApiUpdateIntegrationFeedbacksSettingsRequest,
    type OpenApiUpdateIntegrationFeedbacksSettingsResponseSuccess,
    type OpenApiUpdateIntegrationOrdersSettingsRequest,
    type OpenApiUpdateIntegrationOrdersSettingsResponseSuccess,
    type OpenApiUpdateIntegrationPricesSettingsRequest,
    type OpenApiUpdateIntegrationPricesSettingsResponseSuccess,
    type OpenApiUpdateIntegrationProductsSettingsRequest,
    type OpenApiUpdateIntegrationProductsSettingsResponseSuccess,
    type OpenApiUpdateIntegrationStocksSettingsRequest,
    type OpenApiUpdateIntegrationStocksSettingsResponseSuccess,
} from '#shared/api/schema';
import { encodeSearchParams } from '#shared/lib/url';

const IntegrationFeedbacksTag = 'IntegrationFeedbacksTag' as const;
const IntegrationMappingDiscoveryTag = 'IntegrationMappingDiscoveryTag' as const;
const IntegrationMappingTag = 'IntegrationMappingTag' as const;
const IntegrationSettingsTag = 'IntegrationSettingsTag' as const;

type ServerError = {
    responseData: OpenApiImportProductsAsyncResponses['422']['application/json'];
};

const isServerError = (error: unknown): error is ServerError =>
    typeof get(error, ['responseData', 'type']) !== 'undefined';

const getIntegrationSettingsTag = (
    syncType: Extract<OpenApiSchemaIntegrationSyncType, 'orders' | 'prices' | 'products' | 'stocks'>
): { type: typeof IntegrationSettingsTag; id: string } => {
    return { type: IntegrationSettingsTag, id: syncType };
};

const integrationApi = createApi({
    name: 'entities/integration/api/api',
    tagTypes: [
        IntegrationFeedbacksTag,
        IntegrationMappingDiscoveryTag,
        IntegrationMappingTag,
        IntegrationSettingsTag,
        DELIVERY_WAREHOUSES_GLOBAL_TAG,
    ],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getPublicApiKey: builder.query<OpenApiGetPublicAPIKeyResponseSuccess, OpenApiGetPublicAPIKeySearchParams>({
                query: (params) => {
                    return {
                        url: `/publicapi/apikey`,
                        params,
                        method: 'GET',
                    };
                },
            }),
            getLastExternalModules: builder.query<
                OpenApiGetLastExternalModulesResponseSuccess,
                OpenApiGetLastExternalModulesRequest
            >({
                query: (body) => {
                    return {
                        url: OpenApiGetLastExternalModulesUrl,
                        method: OpenApiGetLastExternalModulesMethod,
                        body,
                    };
                },
            }),
            start: builder.mutation<
                OpenApiIntegrationsStartTypeResponseSuccess,
                { type: Exclude<OpenApiIntegrationsStartTypePathParameters['type'], 'mc_feed' | 'mapping'> }
            >({
                query: (params) => {
                    return {
                        url: getOpenApiIntegrationsStartTypeUrl(params),
                        method: OpenApiIntegrationsStartTypeMethod,
                    };
                },
            }),
            startMCFeed: builder.mutation<
                OpenApiMerchantCenterFeedSyncResponseSuccess,
                OpenApiMerchantCenterFeedSyncRequest
            >({
                query: () => {
                    return {
                        url: OpenApiMerchantCenterFeedSyncUrl,
                        method: OpenApiMerchantCenterFeedSyncMethod,
                    };
                },
            }),
            startIntegrationFeedbacks: builder.mutation<
                OpenApiStartFeedbacksSyncResponseSuccess,
                OpenApiStartFeedbacksSyncPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiStartFeedbacksSyncUrl(params),
                        method: OpenApiStartFeedbacksSyncMethod,
                    };
                },
            }),
            startDryMapping: builder.mutation<
                OpenApiStartDryMappingIntegrationResponseSuccess,
                {
                    params: OpenApiStartDryMappingIntegrationPathParameters;
                    payload: OpenApiStartDryMappingIntegrationRequest;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiStartDryMappingIntegrationUrl(params),
                        method: OpenApiStartDryMappingIntegrationMethod,
                        body: payload,
                        contentType: OpenApiStartDryMappingIntegrationContentType,
                    };
                },
            }),
            startMapping: builder.mutation<
                OpenApiStartMappingIntegrationResponseSuccess,
                OpenApiStartMappingIntegrationPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiStartMappingIntegrationUrl(params),
                        method: OpenApiStartMappingIntegrationMethod,
                    };
                },
            }),
            getIntegrationsTypeStatus: builder.query<
                OpenApiGetIntegrationsTypeStatusResponseSuccess,
                OpenApiGetIntegrationsTypeStatusSearchParams
            >({
                query: (searchParams) => {
                    return {
                        url: `${OpenApiGetIntegrationsTypeStatusUrl}${encodeSearchParams(searchParams)}`,
                        method: OpenApiGetIntegrationsTypeStatusMethod,
                    };
                },
            }),
            cancelExcelIntegration: builder.mutation<
                OpenApiCancelExcelImportProductsResponseSuccess,
                { integration_id: string }
            >({
                query: ({ integration_id }) => {
                    return {
                        url: getOpenApiCancelExcelImportProductsUrl({ id: integration_id }),
                        method: OpenApiCancelExcelImportProductsMethod,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: newData } = queryFulfilledResult;

                    integrationApi.endpoints.getIntegrationsTypeStatus.updateQueryData(
                        { sync_type: ['excel'] },
                        (draft) =>
                            draft.map((item) => {
                                if (item.id === newData.id) {
                                    return newData;
                                }

                                return item;
                            })
                    );
                },
            }),
            importFromExcelAsync: builder.mutation<
                | OpenApiImportProductsAsyncResponseSuccess
                | OpenApiImportProductsAsyncResponses['422']['application/json'],
                { payload: FormData }
            >({
                queryFn: async ({ payload }, api, extraOptions) => {
                    const formDataBaseQuery = createBaseQuery(apiFormDataRequest);
                    const result = await formDataBaseQuery(
                        {
                            url: '/products/import/async',
                            method: 'POST',
                            body: payload,
                        },
                        api,
                        extraOptions
                    );

                    if (isServerError(result.error)) {
                        return { data: result.error.responseData };
                    }

                    return { data: result.data as OpenApiImportProductsAsyncResponseSuccess };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: newData } = queryFulfilledResult;

                    if (typeof newData?.status !== 'number') {
                        integrationApi.endpoints.getIntegrationsTypeStatus.updateQueryData(
                            { sync_type: ['excel'] },
                            (draft) => [newData, ...draft]
                        );
                    }
                },
            }),
            getWarehouses: builder.query<
                OpenApiGetIntegrationWarehousesResponseSuccess,
                OpenApiGetIntegrationWarehousesSearchParams
            >({
                query: ({ integration_type }) => {
                    return {
                        url: `/integrations/type/warehouses?integration_type=` + integration_type,
                        method: 'GET',
                    };
                },
            }),
            getSettingsStocks: builder.query<
                OpenApiGetIntegrationStocksSettingsResponseSuccess,
                OpenApiGetIntegrationStocksSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/settings/stocks',
                        method: 'GET',
                    };
                },
            }),
            getSettingsProducts: builder.query<
                OpenApiGetIntegrationProductsSettingsResponseSuccess,
                OpenApiGetIntegrationProductsSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/settings/products',
                        method: 'GET',
                    };
                },
            }),
            getSettingsPrices: builder.query<
                OpenApiGetIntegrationPricesSettingsResponseSuccess,
                OpenApiGetIntegrationPricesSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/settings/prices',
                        method: 'GET',
                    };
                },
            }),
            getSettingsOrders: builder.query<
                OpenApiGetIntegrationOrdersSettingsResponseSuccess,
                OpenApiGetIntegrationOrdersSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/settings/orders',
                        method: 'GET',
                    };
                },
                providesTags: () => [getIntegrationSettingsTag('orders')],
            }),
            getSettingsFeedbacks: builder.query<
                OpenApiGetIntegrationFeedbacksSettingsResponseSuccess,
                OpenApiGetIntegrationFeedbacksSettingsRequest
            >({
                query: () => {
                    return {
                        url: OpenApiGetIntegrationFeedbacksSettingsUrl,
                        method: OpenApiGetIntegrationFeedbacksSettingsMethod,
                    };
                },
            }),
            updateSettingsFeedbacks: builder.mutation<
                OpenApiUpdateIntegrationFeedbacksSettingsResponseSuccess,
                {
                    params: OpenApiUpdateIntegrationFeedbacksSettingsPathParameters;
                    payload: OpenApiUpdateIntegrationFeedbacksSettingsRequest;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiUpdateIntegrationFeedbacksSettingsUrl(params),
                        method: OpenApiUpdateIntegrationFeedbacksSettingsMethod,
                        body: payload,
                    };
                },
                onQueryStarted: async ({ params }, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data: result } = queryFulfilledResult;

                    integrationApi.endpoints.getSettingsFeedbacks.updateQueryData(undefined, (draft) => {
                        return {
                            ...draft,
                            items: draft.items.map((item) => {
                                if (item.system_type === params.integration_type) {
                                    return {
                                        ...item,
                                        ...result,
                                    };
                                }

                                return item;
                            }),
                        };
                    });
                },
            }),
            updateSettingsStocks: builder.mutation<
                OpenApiUpdateIntegrationStocksSettingsResponseSuccess,
                OpenApiUpdateIntegrationStocksSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/settings/stocks',
                        method: 'POST',
                        body: payload,
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    integrationApi.endpoints.getSettingsStocks.updateQueryData(undefined, (draft) => {
                        return {
                            ...draft,
                        };
                    });
                },
                invalidatesTags: (_, error) =>
                    /**
                     * При изменении настроек остатков, инвалидируем настройки заказов и список складов т.к. они зависят от остатков и могут быть изменены на стороне бэкенда
                     */
                    [getIntegrationSettingsTag('orders'), ...(error ? [] : [DELIVERY_WAREHOUSES_GLOBAL_TAG])],
            }),
            updateSettingsOrders: builder.mutation<
                OpenApiUpdateIntegrationOrdersSettingsResponseSuccess,
                OpenApiUpdateIntegrationOrdersSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/settings/orders',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            updateSettingsProducts: builder.mutation<
                OpenApiUpdateIntegrationProductsSettingsResponseSuccess,
                OpenApiUpdateIntegrationProductsSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/settings/products',
                        method: 'POST',
                        body: payload,
                    };
                },
                onQueryStarted: async (payload, { queryFulfilled }) => {
                    const previousSystemType =
                        integrationApi.endpoints.getSettingsProducts.getCachedValue(undefined)?.data?.system_type;

                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    integrationApi.endpoints.getSettingsProducts.updateQueryData(undefined, (draft) => {
                        return {
                            ...draft,
                            ...payload,
                        };
                    });

                    const mappingTagsToInvalidate: Array<{ type: typeof IntegrationMappingTag; id: string }> = [];

                    if (payload.system_type) {
                        mappingTagsToInvalidate.push({
                            type: IntegrationMappingTag,
                            id: payload.system_type,
                        });

                        if (previousSystemType && previousSystemType !== payload.system_type) {
                            mappingTagsToInvalidate.push({
                                type: IntegrationMappingTag,
                                id: previousSystemType,
                            });
                        }
                    } else if (previousSystemType) {
                        mappingTagsToInvalidate.push({
                            type: IntegrationMappingTag,
                            id: previousSystemType,
                        });
                    }

                    if (mappingTagsToInvalidate.length > 0) {
                        integrationApi.util.invalidateTags(mappingTagsToInvalidate);
                    }
                },
            }),
            updateSettingsPrices: builder.mutation<
                OpenApiUpdateIntegrationPricesSettingsResponseSuccess,
                OpenApiUpdateIntegrationPricesSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/settings/prices',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            deleteSettings: builder.mutation<
                OpenApiDeleteIntegrationSettingsResponseSuccess,
                OpenApiSchemaIntegrationSyncType
            >({
                query: (syncType) => {
                    return {
                        url: `/integrations/type/${syncType}/settings`,
                        method: 'DELETE',
                    };
                },
                invalidatesTags: (_, error, syncType) =>
                    /**
                     * При изменении настроек остатков, инвалидируем список складов
                     */
                    !error && syncType === 'stocks' ? [DELIVERY_WAREHOUSES_GLOBAL_TAG] : [],
            }),
            validateCredentials: builder.mutation<
                OpenApiUpdateIntegrationCredentialsResponseSuccess,
                {
                    params: OpenApiUpdateIntegrationCredentialsPathParameters;
                    payload: OpenApiUpdateIntegrationCredentialsRequest;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiUpdateIntegrationCredentialsUrl(params),
                        method: OpenApiUpdateIntegrationCredentialsMethod,
                        body: payload,
                    };
                },
            }),
            getIntegrationMappingSettings: builder.query<
                OpenApiGetIntegrationMappingSettingsResponseSuccess,
                OpenApiGetIntegrationMappingSettingsPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiGetIntegrationMappingSettingsUrl(params),
                        method: OpenApiGetIntegrationMappingSettingsMethod,
                    };
                },
                providesTags: (_result, _error, { integration_type }) => [
                    { type: IntegrationMappingTag, id: integration_type },
                ],
            }),
            getMappingDiscoveryResult: builder.query<
                OpenApiGetMappingDiscoveryResultResponseSuccess,
                OpenApiGetMappingDiscoveryResultPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiGetMappingDiscoveryResultUrl(params),
                        method: OpenApiGetMappingDiscoveryResultMethod,
                    };
                },
                providesTags: (_result, _error, { integration_type }) => [
                    { type: IntegrationMappingDiscoveryTag, id: integration_type },
                ],
            }),
            startMappingDiscovery: builder.mutation<
                OpenApiStartMappingDiscoveryResponseSuccess,
                OpenApiStartMappingDiscoveryPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiStartMappingDiscoveryUrl(params),
                        method: OpenApiStartMappingDiscoveryMethod,
                    };
                },
                invalidatesTags: (_result, error, { integration_type }) =>
                    error ? [] : [{ type: IntegrationMappingDiscoveryTag, id: integration_type }],
            }),
            setIntegrationMappingSettings: builder.mutation<
                OpenApiSetIntegrationMappingSettingsResponseSuccess,
                {
                    params: OpenApiSetIntegrationMappingSettingsPathParameters;
                    payload: OpenApiSetIntegrationMappingSettingsRequest;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiSetIntegrationMappingSettingsUrl(params),
                        method: OpenApiSetIntegrationMappingSettingsMethod,
                        body: payload,
                    };
                },
                onQueryStarted: async ({ params, payload }, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    integrationApi.endpoints.getIntegrationMappingSettings.updateQueryData(params, (draft) => {
                        return {
                            ...draft,
                            ...payload,
                        };
                    });

                    integrationApi.endpoints.getSettingsProducts.updateQueryData(undefined, (draft) => {
                        if (draft.system_type !== params.integration_type) {
                            return draft;
                        }

                        return {
                            ...draft,
                            mapping: payload,
                        };
                    });
                },
            }),
        };
    },
});

export const useGetIntegrationSettingsStocks = integrationApi.useGetSettingsStocksQuery;
export const useLazyGetIntegrationSettingsStocks = integrationApi.useLazyGetSettingsStocksQuery;
export const lazyGetIntegrationSettingsStocks = integrationApi.endpoints.getSettingsStocks.useQuery;
export const useGetIntegrationSettingsProducts = integrationApi.useGetSettingsProductsQuery;
export const useLazyGetIntegrationSettingsProducts = integrationApi.useLazyGetSettingsProductsQuery;
export const useGetIntegrationSettingsPrices = integrationApi.useGetSettingsPricesQuery;
export const useLazyGetIntegrationSettingsPrices = integrationApi.useLazyGetSettingsPricesQuery;
export const useGetIntegrationSettingsOrders = integrationApi.useGetSettingsOrdersQuery;
export const useLazyGetIntegrationSettingsOrders = integrationApi.useLazyGetSettingsOrdersQuery;
export const useGetIntegrationSettingsFeedbacks = integrationApi.useGetSettingsFeedbacksQuery;
export const useLazyGetIntegrationSettingsFeedbacks = integrationApi.useLazyGetSettingsFeedbacksQuery;
export const useUpdateIntegrationSettingsFeedbacks = integrationApi.useUpdateSettingsFeedbacksMutation;
export const updateIntegrationSettingsFeedbacks = integrationApi.endpoints.updateSettingsFeedbacks.mutate;
export const startIntegrationFeedbacks = integrationApi.endpoints.startIntegrationFeedbacks.mutate;
export const startIntegrationDryMapping = integrationApi.endpoints.startDryMapping.mutate;
export const startIntegrationMapping = integrationApi.endpoints.startMapping.mutate;

export const updateIntegrationSettingsStocks = integrationApi.endpoints.updateSettingsStocks.mutate;
export const getIntegrationSettingsStocks = integrationApi.endpoints.getSettingsStocks.query;
export const updateIntegrationSettingsOrders = integrationApi.endpoints.updateSettingsOrders.mutate;
export const updateIntegrationSettingsProducts = integrationApi.endpoints.updateSettingsProducts.mutate;
export const updateIntegrationSettingsPrices = integrationApi.endpoints.updateSettingsPrices.mutate;
export const getIntegrationSettingsPrices = integrationApi.endpoints.getSettingsPrices.query;

export const useIntegrationLazyGetPublicApiKeyQuery = integrationApi.useLazyGetPublicApiKeyQuery;
export const useGetIntegrationLastExternalModules = integrationApi.useGetLastExternalModulesQuery;
export const deleteIntegrationSettings = integrationApi.endpoints.deleteSettings.mutate;
export const useGetIntegrationStatuses = integrationApi.useGetIntegrationsTypeStatusQuery;
export const useLazyGetIntegrationStatus = integrationApi.useLazyGetIntegrationsTypeStatusQuery;
export const useGetIntegrationWarehouses = integrationApi.useGetWarehousesQuery;
export const startIntegration = integrationApi.endpoints.start.mutate;
export const startIntegrationMCFeed = integrationApi.endpoints.startMCFeed.mutate;
export const validateIntegrationCredentials = integrationApi.endpoints.validateCredentials.mutate;

export const cancelExcelIntegration = integrationApi.endpoints.cancelExcelIntegration.mutate;
export const useIntegrationImportFromExcelAsync = integrationApi.useImportFromExcelAsyncMutation;
export const getIntegrationSettingsProducts = integrationApi.endpoints.getSettingsProducts.query;
export const useGetIntegrationMappingSettings = integrationApi.useGetIntegrationMappingSettingsQuery;
// TODO SS-21171 Убрать исключение после подключения автоматического сопоставления к модалкам.
/**
 * Возвращает состояние автоматического сопоставления параметров.
 *
 * @public
 */
export const useGetIntegrationMappingDiscoveryResult = integrationApi.useGetMappingDiscoveryResultQuery;
// TODO SS-21171 Убрать исключение после подключения автоматического сопоставления к модалкам.
/**
 * Лениво запрашивает состояние автоматического сопоставления параметров.
 *
 * @public
 */
export const useLazyGetIntegrationMappingDiscoveryResult = integrationApi.useLazyGetMappingDiscoveryResultQuery;
// TODO SS-21171 Убрать исключение после подключения автоматического сопоставления к модалкам.
/**
 * Запускает автоматическое сопоставление параметров.
 *
 * @public
 */
export const startIntegrationMappingDiscovery = integrationApi.endpoints.startMappingDiscovery.mutate;
export const setIntegrationMappingSettings = integrationApi.endpoints.setIntegrationMappingSettings.mutate;
